import { useEffect, useMemo, useRef, useState } from 'react';
import useApi from 'hooks/useApi';
import {
  deriveJumioVerification,
  JumioVerification,
  JumioVerificationMap
} from 'components/JumioVerificationBadge';

const idOf = (resource: any): string =>
  String(resource?.resourceDataId ?? resource?.id ?? resource?._id ?? resource?.uid ?? '');

/**
 * Resolve the Jumio verification state for a list of resources shown in a
 * picker, using whichever source is cheapest per resource:
 *
 * - Directory search results arrive with their resource type populated, so the
 *   state is derived locally and appears with no extra request.
 * - Resources restored from an event snapshot ({id, fields, category}) carry no
 *   type definition, so those ids — and only those — are resolved in a single
 *   batch call.
 *
 * Returns a lookup taking the resource object, so callers stay one line.
 */
export default function useJumioVerifications(resources: any[]): (resource: any) => JumioVerification | undefined {
  const { getVerificationStatusBatch } = useApi();
  const [fetched, setFetched] = useState<JumioVerificationMap>({});

  // Held in a ref, NOT an effect dependency: ApiContext rebuilds its value object
  // on every render, so depending on the function directly would refire the
  // lookup on every unrelated provider render.
  const fetchRef = useRef(getVerificationStatusBatch);
  fetchRef.current = getVerificationStatusBatch;

  // Locally derivable states, keyed by id. Recomputed whenever the list changes.
  const local = useMemo(() => {
    const map: Record<string, JumioVerification> = {};
    (resources || []).forEach((r) => {
      const id = idOf(r);
      if (!id) return;
      const derived = deriveJumioVerification(r);
      if (derived.state !== 'unknown') map[id] = derived;
    });
    return map;
  }, [resources]);

  // Only the ids local derivation could not settle, and which we haven't
  // already fetched — so re-rendering a picker doesn't re-request.
  const pendingKey = useMemo(() => {
    const ids = new Set<string>();
    (resources || []).forEach((r) => {
      const id = idOf(r);
      if (!id || local[id] || fetched[id]) return;
      ids.add(id);
    });
    return Array.from(ids).sort().join(',');
  }, [resources, local, fetched]);

  useEffect(() => {
    if (!pendingKey) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await fetchRef.current?.(pendingKey.split(','));
        const data = res?.data;
        if (cancelled || !data || typeof data !== 'object') return;
        // Only commit when something actually resolved — an empty merge would
        // hand back a new object identity, re-running the pendingKey memo for
        // nothing.
        const keys = Object.keys(data);
        if (keys.length > 0) setFetched((prev) => ({ ...prev, ...data }));
      } catch {
        // Best-effort — an unresolved resource simply shows no badge.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingKey]);

  return (resource: any) => {
    const id = idOf(resource);
    if (!id) return undefined;
    return local[id] ?? fetched[id];
  };
}
